import { useEffect, useState } from 'react';
import SiteFooter from '../components/SiteFooter';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChefHat,
  TicketPercent,
  ShoppingCart,
  Copy,
  Check,
  Receipt,
  Share2,
  AlertCircle,
  Home,
  RefreshCw,
  ChevronDown,
  User,
  Store,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import MerchantDashboard from '../components/MerchantDashboard';
import LanguageSwitcher from '../components/LanguageSwitcher';

interface Voucher {
  id: number;
  amount: number;
  /** epoch seconds */
  expiresAt: number;
  expiryDate: string;
  daysRemaining: number;
  minOrderAmount: number;
  sourceEvent: string;
  sourceOrderId: number | null;
  sourceOrderNum: string | null;
}

interface OrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
}

interface RecentOrder {
  orderNum: string;
  totalPrice: number;
  voucherDiscount: number;
  paymentConfirmed: number;
  status: string;
  /** epoch seconds */
  createdAt: number;
  items?: OrderItem[];
}

interface MyDashboardData {
  /** true = 舊式無 token 連結，僅回非敏感摘要（無券/訂單歷史） */
  limited?: boolean;
  vouchers: Voucher[];
  recentOrders: RecentOrder[];
  referralCode: string | null;
  shareUrl: string | null;
  isMerchant?: boolean;
  merchantCode?: string | null;
  merchantName?: string | null;
}

export default function MyDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const merchantToken = searchParams.get('token') || '';
  const [data, setData] = useState<MyDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [expandedOrderNum, setExpandedOrderNum] = useState<string | null>(null);
  const [showAllVouchers, setShowAllVouchers] = useState(false);
  const [view, setView] = useState<'customer' | 'merchant'>('customer');
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({
    repurchase_voucher_enabled: true,
  });

  useEffect(() => {
    apiFetch('/api/public/feature-flags')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.flags) {
          setFeatureFlags(prev => ({ ...prev, ...data.flags }));
        }
      })
      .catch(() => { /* ignore */ });
  }, []);

  // Merchant users should land on merchant dashboard by default
  useEffect(() => {
    if (data?.isMerchant) {
      setView('merchant');
    }
  }, [data?.isMerchant]);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setNotFound(false);

    const load = async () => {
      try {
        // 帶 token 才可取得完整數據（券/訂單歷史）；無 token 只回非敏感摘要
        const tokenQuery = merchantToken ? `?token=${encodeURIComponent(merchantToken)}` : '';
        const res = await apiFetch(`/api/public/my/${encodeURIComponent(slug)}${tokenQuery}`);
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          throw new Error(t('mydash:my.loadFailedStatus', { status: res.status }));
        }
        const json: MyDashboardData = await res.json();
        setData(json);
      } catch (e) {
        setError(e instanceof Error ? e.message : t('mydash:my.loadFailedRetry'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, reloadKey, merchantToken]);

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — 部分瀏覽器不支援 clipboard API
    }
  };

  // ===== Loading skeleton =====
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md mx-auto px-4 py-8 animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700" />
            <div className="space-y-2">
              <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
          <div className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 mb-3" />
          <div className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700 mb-6" />
          <div className="h-14 rounded-xl bg-gray-200 dark:bg-gray-700 mb-6" />
          <div className="h-48 rounded-2xl bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    );
  }

  // ===== 404 — 連結無效 =====
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('mydash:my.notFoundTitle')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {t('mydash:my.notFoundBody')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700 transition"
          >
            <Home size={18} /> {t('mydash:my.backHome')}
          </Link>
        </div>
      </div>
    );
  }

  // ===== 錯誤 + 重試 =====
  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full text-center">
          <AlertCircle size={48} className="mx-auto text-red-300 dark:text-red-800 mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('mydash:my.loadFailedTitle')}</h1>
          <p className="text-sm text-red-600 dark:text-red-400 mb-6">{error || t('mydash:my.noData')}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setReloadKey(k => k + 1)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700 transition"
            >
              <RefreshCw size={18} /> {t('mydash:my.retry')}
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              <Home size={18} /> {t('mydash:my.backHome')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const vouchers = Array.isArray(data.vouchers) ? data.vouchers : [];
  const recentOrders = (Array.isArray(data.recentOrders) ? data.recentOrders : []).slice(0, 5);
  const shareUrl = data.referralCode
    ? (data.shareUrl || `${window.location.origin}/?gs=${encodeURIComponent(data.referralCode)}`)
    : '';
  const isMerchant = !!data.isMerchant;
  // 日期格式跟隨介面語言（zh-HK → 日/月/年；en → 日/月/年 英文格式）
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'zh-HK';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-gray-100">
      <div className={`mx-auto px-4 py-8 pb-16 ${isMerchant && view === 'merchant' ? 'max-w-3xl' : 'max-w-md'}`}>
        {/* ===== 頁頭 ===== */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-brand-600 rounded-full flex items-center justify-center text-white shadow-lg flex-shrink-0">
            <ChefHat size={24} />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('mydash:my.brand')}</p>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('mydash:my.accountTitle')}</h1>
          </div>
          <div className="ml-auto">
            <LanguageSwitcher size="sm" />
          </div>
        </div>

        {/* ===== 無 token 降級提示（舊式連結只顯示摘要） ===== */}
        {data.limited && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl text-sm text-amber-800 dark:text-amber-300">
            {t('mydash:my.limitedNote')}
          </div>
        )}

        {/* ===== 客戶 / 商家 切換 ===== */}
        {isMerchant && (
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setView('customer')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                view === 'customer'
                  ? 'bg-brand-600 text-white shadow'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <User size={16} /> {t('mydash:my.accountTitle')}
            </button>
            <button
              type="button"
              onClick={() => setView('merchant')}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition ${
                view === 'merchant'
                  ? 'bg-brand-600 text-white shadow'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Store size={16} /> {t('mydash:my.viewMerchant')}
            </button>
          </div>
        )}

        {view === 'merchant' && slug ? (
          <MerchantDashboard
            slug={slug}
            merchantCode={data.merchantCode}
            merchantName={data.merchantName}
            token={merchantToken}
          />
        ) : (
          <>
            {/* ===== 回贈券區塊 ===== */}
            {featureFlags.repurchase_voucher_enabled && (
            <section className="mb-6">
              <h2 className="text-base font-bold text-gray-900 dark:text-white mb-3">{t('mydash:my.vouchersTitle')}</h2>
              {vouchers.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
                  <TicketPercent size={36} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="font-bold text-gray-700 dark:text-gray-200">{t('mydash:my.vouchersEmpty')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('mydash:my.vouchersHint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(showAllVouchers ? vouchers : vouchers.slice(0, 2)).map(v => {
                    let sourceText = t('mydash:my.source.system');
                    if (v.sourceEvent.startsWith('order_paid')) {
                      sourceText = t('mydash:my.source.orderEarned', { ref: v.sourceOrderNum || v.sourceOrderId || '' });
                    } else if (v.sourceEvent === 'egg_campaign' || v.sourceEvent.startsWith('egg')) {
                      sourceText = t('mydash:my.source.egg');
                    } else if (v.sourceEvent === 'manual' || v.sourceEvent === 'admin') {
                      sourceText = t('mydash:my.source.staff');
                    } else if (v.sourceEvent === 'referral_referrer_reward' || v.sourceEvent === 'referral_referee_reward') {
                      sourceText = t('mydash:my.source.referral');
                    }
                    return (
                      <div
                        key={v.id}
                        className="relative bg-brand-50 dark:bg-orange-900/20 border-2 border-dashed border-brand-300 dark:border-orange-700 rounded-2xl p-5 flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-brand-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow">
                          <TicketPercent size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-2xl font-bold text-brand-700 dark:text-orange-300">
                            {t('mydash:my.voucherAmount', { amount: v.amount })}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                            {t('mydash:my.voucherMinSpend', { min: v.minOrderAmount })}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sourceText}</div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span
                              className={`text-xs font-bold whitespace-nowrap ${
                                v.daysRemaining <= 3
                                  ? 'text-red-600 dark:text-red-400'
                                  : 'text-gray-500 dark:text-gray-400'
                              }`}
                            >
                              {t('mydash:my.daysLeft', { count: v.daysRemaining, date: v.expiryDate })}
                            </span>
                            {v.daysRemaining <= 3 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                {t('mydash:my.expiringSoon')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {vouchers.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setShowAllVouchers(s => !s)}
                      className="w-full py-2 text-sm font-medium text-brand-700 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 transition-colors"
                    >
                      {showAllVouchers ? t('mydash:my.showLessVouchers') : t('mydash:my.moreVouchers', { count: vouchers.length - 2 })}
                    </button>
                  )}
                </div>
              )}
            </section>
            )}

            {/* ===== 立即落單 ===== */}
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-xl font-bold py-4 rounded-xl shadow-lg transition mb-6"
            >
              <ShoppingCart size={24} />
              {t('mydash:my.orderNow')}
            </Link>

            {/* ===== 最近訂單 ===== */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
              <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                <Receipt size={18} className="text-brand-600 dark:text-orange-400" />
                <h2 className="text-base font-bold text-gray-900 dark:text-white">{t('mydash:my.recentOrders')}</h2>
              </div>
              {recentOrders.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('mydash:my.noOrders')}
                </div>
              ) : (
                <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                  {recentOrders.map(o => {
                    const paid = o.totalPrice - (o.voucherDiscount || 0);
                    const items = Array.isArray(o.items) ? o.items : [];
                    const expanded = expandedOrderNum === o.orderNum;
                    return (
                      <li key={o.orderNum} className="px-5 py-4">
                        <button
                          type="button"
                          onClick={() => setExpandedOrderNum(expanded ? null : o.orderNum)}
                          className="w-full flex items-center justify-between gap-3 text-left"
                          aria-expanded={expanded}
                        >
                          <div className="min-w-0 flex items-center gap-2">
                            <ChevronDown
                              size={16}
                              className={`flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
                            />
                            <div className="min-w-0">
                              <div className="font-bold text-gray-900 dark:text-white">#{o.orderNum}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {new Date(o.createdAt * 1000).toLocaleDateString(dateLocale)}
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-bold text-brand-700 dark:text-orange-300">
                              HK${paid}
                              {o.voucherDiscount > 0 && (
                                <span className="ml-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                  {t('mydash:my.voucherDeducted', { amount: o.voucherDiscount })}
                                </span>
                              )}
                            </div>
                            <span
                              className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                                o.paymentConfirmed
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              }`}
                            >
                              {o.paymentConfirmed ? t('mydash:my.statusConfirmed') : t('mydash:my.statusPending')}
                            </span>
                          </div>
                        </button>
                        {expanded && (
                          <div className="mt-3 ml-6 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700 px-4 py-3">
                            {items.length === 0 ? (
                              <p className="text-xs text-gray-400 dark:text-gray-500">{t('mydash:my.noItems')}</p>
                            ) : (
                              <ul className="space-y-1.5">
                                {items.map((it, idx) => (
                                  <li key={idx} className="flex items-center justify-between gap-3 text-sm">
                                    <span className="min-w-0 truncate text-gray-700 dark:text-gray-200">
                                      {it.name}
                                      <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">×{it.quantity}</span>
                                    </span>
                                    <span className="flex-shrink-0 text-gray-600 dark:text-gray-300">
                                      HK${it.unitPrice * it.quantity}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* ===== 推薦好友 ===== */}
            {data.referralCode && (
              <section className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Share2 size={18} className="text-brand-600 dark:text-orange-400" />
                  <h2 className="text-base font-bold text-gray-900 dark:text-white">{t('mydash:my.referralTitle')}</h2>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('mydash:my.referralIntro')}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={shareUrl}
                    onFocus={e => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 outline-none"
                  />
                  <button
                    onClick={() => handleCopy(shareUrl)}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 bg-brand-600 text-white text-sm font-bold rounded-lg hover:bg-brand-700 transition"
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? t('mydash:my.copied') : t('mydash:my.copyLink')}
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
